import { AxiosError } from 'axios'
import DataTableControllerWithoutCol from 'components/common/dataTable/dataTableControllerWithoutCol'
import {
  FILTER_FIELD_PURCHASE_TIMER,
  PurchaseTimerDataTable,
  PurchaseTimerFilterForm,
} from 'components/purchaseTimer'
import { PlusIcon } from 'components/icons'
import { Button } from 'components/common'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { ImportColumn } from 'components/common/importModal'
import { usePageAccess } from 'hooks'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  exportPurchaseTimerData,
  importPurchaseTimerData,
} from 'utils/api/purchaseTimerApi'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import { PAGE_PURCHASE_TIMER_ADD } from 'utils/constants'

const PurchaseTimerListPage = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])

  const navigate = useNavigate()

  useEffect(() => {
    getImportTemplate('purchaseTimer')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('purchaseTimer')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'purchase_timer_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  const handleExport = async () => {
    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await exportPurchaseTimerData()
      toast.success('Export started! File will be sent to your email.', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        // Concurrency guard: a running purchase-timer export or barcode-deletion
        // job returns the running-status reason under errors.msg.runningService.
        const runningService =
          error?.response?.data?.errors?.msg?.runningService
        if (runningService) {
          return toast.error(runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to start Purchase Timer export.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Purchase Timer</title>
      </Helmet>

      {isEditor && (
        <div className="flex items-center gap-2">
          <Button
            buttonText="Add Purchase Timer"
            icon={<PlusIcon />}
            className="mb-5"
            onClick={() => navigate(PAGE_PURCHASE_TIMER_ADD.path)}
          />
        </div>
      )}

      <div className="mb-2 flex flex-wrap items-center justify-between md:mb-0 md:flex-nowrap">
        <h3 className="title">Purchase Timer</h3>
      </div>

      <DataTableControllerWithoutCol
        isEditor={isEditor}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleImport={importPurchaseTimerData}
        placeholder="Search by Barcode, Store"
        handleExport={handleExport}
        loading={loading}
        importColumns={importColumns}
        importModuleName="purchase timer"
        onDownloadSample={handleDownloadSample}
      />

      {!showFilter && (
        <ActiveFilterChip filterKey={FILTER_FIELD_PURCHASE_TIMER} />
      )}

      {showFilter ? (
        <PurchaseTimerFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <PurchaseTimerDataTable
          isEditor={isEditor}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default PurchaseTimerListPage
